import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useMemo, useEffect } from 'react';
import { useProject } from '@/hooks/useProjects';
import { useEnvironments } from '@/hooks/useEnvironments';
import { useSecrets } from '@/hooks/useSecrets';
import { useOrganization } from '@/hooks/useOrganization';
import { StatusDashboard } from '@/components/projects/StatusDashboard';
import { EnvironmentTabs } from '@/components/projects/EnvironmentTabs';
import { ComparisonTable } from '@/components/projects/ComparisonTable';
import { BulkActions } from '@/components/projects/BulkActions';
import { ImportModal } from '@/components/projects/ImportModal';
import { Input } from '@/components/ui/input';
import { Search, ChevronRightIcon } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useToast } from '@/hooks/useToast';
import { calculateEnvironmentStats } from '@/lib/variable-utils';

export const Route = createFileRoute('/_authenticated/org/$orgId/projects/$id/')({
  component: ProjectDetailPage,
});

function ProjectDetailPage() {
  const { id: projectId, orgId } = Route.useParams();
  const { t } = useTranslation();
  const toast = useToast();
  const { organization } = useOrganization(orgId);
  const { project, isLoading: projectLoading } = useProject(projectId, organization?.id);
  const environmentsQuery = useEnvironments(projectId);
  const secretsQuery = useSecrets(projectId);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVariables, setSelectedVariables] = useState<string[]>([]);
  const [selectedEnvironments, setSelectedEnvironments] = useState<string[]>([]);
  const [showImportModal, setShowImportModal] = useState(false);

  const environments = environmentsQuery.data?.environments || [];
  const secrets = secretsQuery.data?.secrets || [];

  // Inicializar entornos seleccionados (solo una vez cuando se cargan los entornos)
  const [hasInitializedEnvironments, setHasInitializedEnvironments] = useState(false);
  
  useEffect(() => {
    if (environments.length > 0 && !hasInitializedEnvironments && selectedEnvironments.length === 0) {
      const slugs = environments.map((env: { slug: string }) => env.slug);
      setSelectedEnvironments(slugs);
      setHasInitializedEnvironments(true);
    }
  }, [environments, hasInitializedEnvironments, selectedEnvironments.length]);

  // Procesar secretos para crear estructura de variables
  const variables = useMemo(() => {
    const variableMap = new Map<string, {
      key: string;
      description: string | null;
      values: Record<string, string | null>;
      secretIds: Record<string, string>;
      lastUpdated: Record<string, string | null>;
    }>();

    // First pass: process environment-specific secrets (they take priority)
    for (const secret of secrets) {
      if (secret.environmentId) {
        const env = environments.find((e: { id: string; slug: string }) => e.id === secret.environmentId);
        const envSlug = env?.slug;
        if (!envSlug) continue; // Skip if environment not found

        if (!variableMap.has(secret.key)) {
          variableMap.set(secret.key, {
            key: secret.key,
            description: secret.description || null,
            values: {},
            secretIds: {},
            lastUpdated: {},
          });
        }

        const variable = variableMap.get(secret.key);
        if (variable) {
          // Convert empty strings to null for proper "missing" state detection
          const processedValue = secret.value && secret.value.trim() !== '' ? secret.value : null;
          // Environment-specific secrets always override
          variable.values[envSlug] = processedValue;
          variable.secretIds[envSlug] = secret.id;
          variable.lastUpdated[envSlug] = secret.updatedAt || secret.createdAt || null;
          
          // Update description if not set and secret has one
          if (!variable.description && secret.description) {
            variable.description = secret.description;
          }
        }
      }
    }

    // Second pass: process project-level secrets (fill in missing values)
    for (const secret of secrets) {
      if (!secret.environmentId) {
        // Handle project-level secrets (scope: 'project', no environmentId)
        // These should appear in all environments where no environment-specific value exists
        if (!variableMap.has(secret.key)) {
          variableMap.set(secret.key, {
            key: secret.key,
            description: secret.description || null,
            values: {},
            secretIds: {},
            lastUpdated: {},
          });
        }

        const variable = variableMap.get(secret.key);
        if (variable) {
          // Convert empty strings to null for proper "missing" state detection
          const processedValue = secret.value && secret.value.trim() !== '' ? secret.value : null;
          
          // Apply this value only to environments where no value is set yet
          environments.forEach((env: { slug: string }) => {
            // Only set if no environment-specific value exists for this environment
            if (!(env.slug in variable.values) || variable.values[env.slug] === null) {
              variable.values[env.slug] = processedValue;
            }
            // Use the same secret ID for all environments where project-level secret applies
            if (!(env.slug in variable.secretIds) || variable.secretIds[env.slug] === secret.id) {
              variable.secretIds[env.slug] = secret.id;
            }
            if (!(env.slug in variable.lastUpdated) || 
                (variable.values[env.slug] === processedValue && variable.lastUpdated[env.slug])) {
              variable.lastUpdated[env.slug] = secret.updatedAt || secret.createdAt || null;
            }
          });
          
          // Update description if not set and secret has one
          if (!variable.description && secret.description) {
            variable.description = secret.description;
          }
        }
      }
    }

    // Ensure all variable-environment combinations are initialized
    const result = Array.from(variableMap.values());
    
    // Initialize missing environment values as null
    result.forEach(variable => {
      environments.forEach((env: { slug: string }) => {
        if (!(env.slug in variable.values)) {
          variable.values[env.slug] = null;
        }
      });
    });
    
    return result;
  }, [secrets, environments]);

  const handleEnvironmentToggle = (envSlug: string) => {
    setSelectedEnvironments(prev =>
      prev.includes(envSlug)
        ? prev.filter(s => s !== envSlug)
        : [...prev, envSlug]
    );
  };

  const handleVariableSelect = (variableKey: string) => {
    setSelectedVariables(prev =>
      prev.includes(variableKey)
        ? prev.filter(k => k !== variableKey)
        : [...prev, variableKey]
    );
  };

  const handleRefetch = () => {
    environmentsQuery.refetch();
    secretsQuery.refetch();
  };

  const handleExport = () => {
    if (!project) {
      return;
    }
    
    if (selectedEnvironments.length === 0) {
      return;
    }

    // Generate export content for selected environments
    const exportContent = selectedEnvironments.map(envSlug => {
      const env = environments.find((e: { slug: string }) => e.slug === envSlug);
      if (!env) return '';

      const envVariables = variables
        .filter(variable => variable.values[envSlug] !== null)
        .map(variable => `${variable.key}=${variable.values[envSlug]}`)
        .join('\n');

      return `# ${env.name}\n${envVariables}`;
    }).filter(content => content.trim() !== '').join('\n\n');

    if (!exportContent) {
      toast.error(t('toast.error.noVariablesToExport'));
      return;
    }

    // Create and download file
    const blob = new Blob([exportContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${project.name}-${selectedEnvironments.join('-')}-export.env`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success(t('toast.success.exported'));
  };

  const isLoading = projectLoading || environmentsQuery.isLoading || secretsQuery.isLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Project not found</h2>
          <p className="text-gray-600 mt-2">The project you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  const projectData = {
    environments,
    variables,
  };

  // Calculate environment stats for EnvironmentTabs
  const getEnvironmentStats = (envSlug: string) => {
    const stats = calculateEnvironmentStats(variables, envSlug);
    return {
      percentage: stats.percentage,
      isComplete: stats.isComplete,
    };
  };

  const environmentStats = environments.reduce((acc, env) => {
    acc[env.slug] = getEnvironmentStats(env.slug);
    return acc;
  }, {} as Record<string, { percentage: number; isComplete: boolean }>);

  return (
    <div className="min-h-screen bg-gray-50 p-6 space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-gray-500">
        <Link 
          to="/org/$orgId" 
          params={{ orgId }}
          className="hover:text-gray-700"
        >
          {organization?.name || 'Organization'}
        </Link>
        <ChevronRightIcon className="w-4 h-4" />
        <Link 
          to="/org/$orgId/projects" 
          params={{ orgId }}
          className="hover:text-gray-700"
        >
          {t('navigation.projects')}
        </Link>
        <ChevronRightIcon className="w-4 h-4" />
        <span className="text-gray-900 font-medium">{project.name}</span>
      </nav>

      {/* Project Tabs removed in favor of left sidebar navigation */}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="mb-4">
          <h1 className="text-3xl font-bold text-gray-900">{project.name}</h1>
          {project.description && (
            <p className="text-gray-600 mt-1">{project.description}</p>
          )}
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            type="text"
            placeholder={t('projectDashboard.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Status Dashboard */}
      {selectedEnvironments.length > 0 && (
        <StatusDashboard
          project={projectData}
          selectedEnvironments={selectedEnvironments}
        />
      )}

      {/* Environment Tabs */}
      <EnvironmentTabs
        environments={environments}
        selectedEnvironments={selectedEnvironments}
        onEnvironmentToggle={handleEnvironmentToggle}
        projectId={projectId}
        onEnvironmentUpdate={handleRefetch}
        environmentStats={environmentStats}
        onImport={() => setShowImportModal(true)}
        onExport={handleExport}
      />

      {/* Bulk Actions */}
      {selectedVariables.length > 0 && (
        <BulkActions
          selectedCount={selectedVariables.length}
          selectedVariables={selectedVariables}
          onAction={(action, vars) => {
            console.log(`Bulk action: ${action}`, vars);
            if (action === 'delete') {
              // TODO: Implement bulk delete
              setSelectedVariables([]);
            }
          }}
        />
      )}

      {/* Comparison Table */}
      <div className="bg-white rounded-lg shadow-sm">
        <ComparisonTable
          variables={variables}
          environments={environments}
          selectedEnvironments={selectedEnvironments}
          searchQuery={searchQuery}
          selectedVariables={selectedVariables}
          onVariableSelect={handleVariableSelect}
          onVariableUpdate={handleRefetch}
          projectId={projectId}
        />
      </div>

      {/* Import Modal */}
      <ImportModal
        open={showImportModal}
        onOpenChange={(open) => {
          setShowImportModal(open);
          if (!open) {
            handleRefetch();
          }
        }}
        environments={environments}
        projectId={projectId}
      />
    </div>
  );
}
